import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const LINKS = [
  { label: "Home", hash: "#home" },
  { label: "About Us", hash: "#about" },
  { label: "Products", hash: "#products" },
  { label: "Certificate", hash: "#certificate" },
  { label: "Contact Us", hash: "#contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const ids = ["home", "about", "products", "certificate", "contact"];
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      obs.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const go = (hash) => {
    setOpen(false);
    setTimeout(() => scrollToSection(hash), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 2.5, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-4 inset-x-4 md:inset-x-10 z-50"
      >
        <div
          className={`flex items-center justify-between rounded-full pl-3 pr-2 sm:pl-4 sm:pr-3 py-2.5 border backdrop-blur-xl transition-colors duration-500 ${
            scrolled
              ? "bg-forest/90 border-gold/30 shadow-2xl"
              : "bg-forest/60 border-gold/20 shadow-lg"
          }`}
        >
          <button
            data-testid="nav-logo-home"
            onClick={() => go("#home")}
            className="flex items-center gap-3 group"
            aria-label="Go to home"
          >
            <span className="w-11 h-11 rounded-full bg-white ring-1 ring-gold/50 overflow-hidden shadow-inner group-hover:scale-105 transition-transform duration-300">
              <img
                src="/assets/nesha-logo.png"
                alt="Nesha Global logo"
                className="w-full h-full object-cover"
              />
            </span>
            <span className="text-left leading-tight">
              <span className="block font-display text-lg tracking-[0.18em] text-cream uppercase">
                Nesha Global
              </span>
              <span className="block font-mono text-[9px] tracking-[0.3em] text-gold uppercase">
                Pvt. Ltd. — Dehradun
              </span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {LINKS.map((l) => (
              <button
                key={l.hash}
                data-testid={`nav-link-${l.hash.slice(1)}`}
                onClick={() => go(l.hash)}
                className={`relative px-4 py-2 font-grotesk text-sm tracking-wide transition-colors duration-300 rounded-full ${
                  active === l.hash.slice(1)
                    ? "text-sunset"
                    : "text-cream/75 hover:text-cream"
                }`}
              >
                {l.label}
                {active === l.hash.slice(1) && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-sunset" />
                )}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              data-testid="nav-phone-link"
              href="tel:+916398888926"
              className="hidden md:flex items-center gap-2 rounded-full bg-sunset px-5 py-2.5 font-grotesk text-sm font-semibold text-forest hover:bg-sunset-bright transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              +91 6398888926
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden flex items-center justify-center w-11 h-11 rounded-full border border-gold/30 text-cream"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 bg-forest/97 backdrop-blur-2xl flex flex-col justify-center px-8 lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <div key={l.hash} className="overflow-hidden">
                  <motion.button
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.5, delay: 0.08 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                    data-testid={`mobile-link-${l.hash.slice(1)}`}
                    onClick={() => go(l.hash)}
                    className="flex items-baseline gap-4 py-2 text-left"
                  >
                    <span className="font-mono text-xs text-sunset">0{i + 1}</span>
                    <span className="font-display text-4xl text-cream uppercase tracking-wide">
                      {l.label}
                    </span>
                  </motion.button>
                </div>
              ))}
            </nav>
            <div className="mt-10 border-t border-gold/20 pt-6 font-mono text-xs text-cream/60 space-y-2">
              <p data-testid="mobile-menu-phone">PH — +91 6398888926</p>
              <p data-testid="mobile-menu-email">MAIL — info@neshaglobal.com</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
